import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, ArrowLeft, Check, CheckCheck, Trash2, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import useChatStream from '../../hooks/useChatStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialsOf = (name) =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

/**
 * Shared WhatsApp-style conversation panel used in CRM, Employee Portal and
 * Customer Portal. Self-contained: fetches messages, sends, typing, read
 * receipts and own-message deletion, and live-updates over the SSE bus.
 *
 * Props:
 *   apiBase   — 'customer' | 'staff'  (REST namespace under /api)
 *   threadId  — the thread to render
 *   onBack    — optional (mobile back button)
 *   onActivity— optional callback after fetch/send (parent refreshes list/badges)
 *   heightClass — tailwind height for the panel (default h-full)
 */
export const ChatConversation = ({ apiBase, threadId, onBack, onActivity, heightClass = 'h-full' }) => {
  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingName, setTypingName] = useState('');
  const scrollRef = useRef(null);
  const lastTypingRef = useRef(0);
  const typingClearRef = useRef(null);
  const activityRef = useRef(onActivity);
  activityRef.current = onActivity;

  const base = `${API_URL}/api/${apiBase}`;

  const fetchMessages = useCallback(async () => {
    if (!threadId) return;
    try {
      const res = await axios.get(`${base}/threads/${threadId}/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
      setThread(res.data.thread || null);
      activityRef.current && activityRef.current();
    } catch { /* noop */ } finally { setLoading(false); }
  }, [threadId, base]);

  useEffect(() => { setLoading(true); setMessages([]); fetchMessages(); }, [threadId, fetchMessages]);

  useChatStream(useCallback((evt) => {
    if (!threadId || evt?.thread_id !== threadId) return;
    if (evt.type === 'thread_message' || evt.type === 'thread_read') {
      fetchMessages();
    } else if (evt.type === 'thread_typing') {
      setTypingName(evt.name || 'Someone');
      clearTimeout(typingClearRef.current);
      typingClearRef.current = setTimeout(() => setTypingName(''), 3500);
    }
  }, [threadId, fetchMessages]));

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typingName]);

  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current < 2500) return;
    lastTypingRef.current = now;
    axios.post(`${base}/threads/${threadId}/typing`, {}, { withCredentials: true }).catch(() => {});
  };

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await axios.post(`${base}/threads/${threadId}/send`, { body }, { withCredentials: true });
      setInput('');
      await fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const del = async (mid) => {
    try {
      await axios.delete(`${base}/messages/${mid}`, { withCredentials: true });
      fetchMessages();
    } catch (e) { toast.error('Could not delete'); }
  };

  const title = thread?.title || 'Conversation';
  const subtitle = thread?.audience === 'host'
    ? `Party host${thread?.child_name ? ` · ${thread.child_name}'s party` : ''}`
    : thread?.audience === 'business' ? 'Business office' : '';
  const isBiz = thread?.audience === 'business';

  return (
    <div className={`flex flex-col ${heightClass} bg-[#FFFDF8] min-h-0`} data-testid="chat-conversation">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-[#E8E5DF] flex-shrink-0">
        {onBack && (
          <button onClick={onBack} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FFFDF8]" data-testid="chat-conv-back" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isBiz ? 'bg-[#118AB2]/12 text-[#118AB2]' : 'bg-[#FF3B6B]/12 text-[#FF3B6B]'}`}>
          {isBiz ? <Building2 className="w-4 h-4" /> : <span className="text-xs font-bold">{initialsOf(title)}</span>}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-[#1E1E24] truncate" data-testid="chat-conv-title">{title}</p>
          {subtitle && <p className="text-[11px] text-[#6B6B7A] truncate">{subtitle}</p>}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-1.5 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#6B6B7A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <p className="text-xs text-center text-[#6B6B7A] py-10">No messages yet. Say hello! 👋</p>
        ) : messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'} group`} data-testid={`chat-msg-${m.message_id}`}>
            <div className={`relative max-w-[82%] px-3 py-2 rounded-2xl text-sm shadow-sm ${m.mine ? 'bg-[#FF3B6B] text-white rounded-br-sm' : 'bg-white border border-[#E8E5DF] text-[#1E1E24] rounded-bl-sm'}`}>
              {!m.mine && (
                <p className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>
              )}
              <p className={`whitespace-pre-wrap break-words ${m.deleted ? 'italic opacity-70' : ''}`}>{m.body}</p>
              <span className={`flex items-center gap-1 justify-end mt-0.5 text-[9px] ${m.mine ? 'text-white/70' : 'text-[#9a9aa6]'}`}>
                {fmtTime(m.created_at)}
                {m.mine && !m.deleted && (m.read ? <CheckCheck className="w-3 h-3" data-testid={`chat-msg-read-${m.message_id}`} /> : <Check className="w-3 h-3" />)}
              </span>
              {m.mine && !m.deleted && (
                <button
                  onClick={() => del(m.message_id)}
                  className="absolute -left-7 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-[#E8E5DF] text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  data-testid={`chat-msg-del-${m.message_id}`}
                  title="Delete message"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {typingName && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-white border border-[#E8E5DF] text-xs text-[#6B6B7A] italic">
              {typingName} is typing…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="p-2 border-t border-[#E8E5DF] bg-white flex gap-2 flex-shrink-0">
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); pingTyping(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Type a message…"
          maxLength={4000}
          className="flex-1 px-3 py-2 border border-[#E8E5DF] rounded-full text-sm outline-none focus:border-[#FF3B6B]"
          data-testid="chat-input"
        />
        <button
          onClick={send}
          disabled={!input.trim() || sending}
          className="w-10 h-10 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white flex items-center justify-center disabled:opacity-50 flex-shrink-0"
          data-testid="chat-send"
          aria-label="Send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default ChatConversation;
